import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import * as Router from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { DashboardLayout } from "component/layout/dashboard";
import { FullPageCard } from "component/shared/full-page-card";
import { PageHeader } from "component/shared/page-header";
import { UnsavedChangesPrompt } from "component/shared/unsaved-changes-prompt";
import { FeatherIcon } from "component/shared/feather-icon";
import { ColdFusionIframe } from "component/shared/iframe/coldfusion";
import { SettingsStickyFooter } from "component/shared/settings-sticky-footer";
import { MultiSelectSearch, MultiSelectOption } from "component/shared/multi-select-search";
import { PageProps } from "shared/page-props";
import { RootState } from "redux/store";
import { getCities, getPages } from "redux/selector";
import { fetchCities } from "redux/slice/cities";
import { fetchPages } from "redux/slice/pages";
import { fetchLocations, saveLocations } from "redux/slice/locations";
import { LocationTypeBoardResource, LocationsResource } from "api/resource/locations";
import { City } from "model/city";
import { PageType } from "type/page-type";
import { coldFusionReplatform } from "shared/env";
import { styles } from "./style";

interface Props extends PageProps, Router.RouteComponentProps, WithStyles<typeof styles> {}

const title = "Locations";

// Two valid city-list formats. The legacy "City, County Sorter" (value 3) has been broken since
// 2015 and is intentionally omitted (MAX-3820).
const CITY_LIST_FORMAT_OPTIONS = [
	{ value: 1, label: "City only" },
	{ value: 2, label: "City and state" },
];

const cityLabel = (city: City) => (city.state ? `${city.name}, ${city.state}` : city.name);

const ColdFusionFallback: React.FC<Props> = ({ user }) => (
	<DashboardLayout
		permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
		title={title}
		header={
			<Mui.Typography variant="h1">
				<FeatherIcon>
					<Icons.Settings />
				</FeatherIcon>
				{title}
			</Mui.Typography>
		}
		iframe={<ColdFusionIframe url="/searchAreas.cfm" />}
	/>
);

// "View included locations (N)" collapsible trigger. Styled per Figma as a tertiary-action (teal /
// primary) text button — SF Pro Semibold 14 with a right-hand chevron — rather than the shared
// Collapsible (which renders plain body text), so the color/weight match the design.
const IncludedLocations: React.FC<{
	count: number;
	locations: string[];
	classes: Record<string, string>;
}> = ({ count, locations, classes }) => {
	const [open, setOpen] = useState(false);
	return (
		<Mui.Box>
			<Mui.Link
				component="button"
				type="button"
				color="primary"
				underline="none"
				className={classes.includedTrigger}
				onClick={() => setOpen((prev) => !prev)}
			>
				{`View included locations (${count})`}
				{open ? <Icons.ChevronUp size={16} /> : <Icons.ChevronDown size={16} />}
			</Mui.Link>
			<Mui.Collapse in={open}>
				<ul className={classes.bulletList}>
					{locations.map((loc, i) => (
						<li key={i}>{loc}</li>
					))}
				</ul>
			</Mui.Collapse>
		</Mui.Box>
	);
};

const NativePageBase: React.FC<Props> = ({ user, classes = {}, history }) => {
	const dispatch = useDispatch();
	const saved = useSelector((state: RootState) => state.locations.locations);
	const loading = useSelector((state: RootState) => state.locations.loading);
	const allCities = useSelector(getCities);
	const pages = useSelector(getPages);

	// Location Types: board list carrying selected + displayName per type (locally editable copy).
	const [boards, setBoards] = useState<LocationTypeBoardResource[]>([]);
	// Cities
	const [customListEnabled, setCustomListEnabled] = useState(false);
	const [restrictToCustomList, setRestrictToCustomList] = useState(false);
	const [cityListFormat, setCityListFormat] = useState(1);
	const [cityIds, setCityIds] = useState<number[]>([]);
	const [isResponsive, setIsResponsive] = useState(false);

	const [dirty, setDirty] = useState(false);
	const [saving, setSaving] = useState(false);
	const [submitted, setSubmitted] = useState(false);
	const [snackbarOpen, setSnackbarOpen] = useState(false);
	const [snackbarMessage, setSnackbarMessage] = useState("");
	const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">("success");

	useEffect(() => {
		dispatch(fetchLocations() as any);
		dispatch(fetchCities() as any);
		dispatch(fetchPages() as any);
	}, [dispatch]);

	const syncFromSaved = (data: LocationsResource) => {
		// Deep-copy boards so local edits don't mutate the Redux snapshot.
		setBoards(data.boards ? data.boards.map((b) => ({
			...b,
			locationTypes: b.locationTypes.map((t) => ({ ...t })),
		})) : []);
		setCustomListEnabled(data.customCityListYn);
		setRestrictToCustomList(data.restrictToCustomList);
		// The API passes Client.CityList_Format through raw, so a legacy/out-of-range value can
		// arrive here. `|| 1` only caught falsy ones, leaving the RadioGroup with no matching
		// option and nothing selected. Coerce anything unsupported to "City only".
		setCityListFormat(
			CITY_LIST_FORMAT_OPTIONS.some((opt) => opt.value === data.cityListFormat)
				? data.cityListFormat
				: 1
		);
		setCityIds(data.cityIds || []);
		setIsResponsive(data.isResponsive);
	};

	useEffect(() => {
		if (saved) {
			syncFromSaved(saved);
			setDirty(false);
			setSubmitted(false);
		}
	}, [saved]);

	const cityOptions: MultiSelectOption[] = useMemo(
		() => allCities.map((c) => ({ id: c.id, label: cityLabel(c) })),
		[allCities]
	);

	// --- Location Type editing ---
	// Key edits by board INDEX, not boardId: an account can be connected to the same board more than
	// once (duplicate boardIds), so matching on boardId would edit every duplicate at once.
	const updateType = (boardIndex: number, metaField: string, patch: { selected?: boolean; displayName?: string }) => {
		setBoards((prev) => prev.map((b, i) => {
			if (i !== boardIndex) {
				return b;
			}
			return {
				...b,
				locationTypes: b.locationTypes.map((t) =>
					t.metaField === metaField ? { ...t, ...patch } : t
				),
			};
		}));
		setDirty(true);
	};

	// --- Validation (mirrors the Figma inline errors) ---
	const blankDisplayNames = useMemo(() => {
		const keys = new Set<string>();
		boards.forEach((b, boardIndex) => b.locationTypes.forEach((t) => {
			if (t.selected && (!t.displayName || t.displayName.trim() === "")) {
				keys.add(`${boardIndex}:${t.metaField}`);
			}
		}));
		return keys;
	}, [boards]);

	const cityError = customListEnabled && cityIds.length === 0;
	const hasErrors = blankDisplayNames.size > 0 || cityError;

	const buildPayload = (): LocationsResource => ({
		boards,
		cityIds,
		customCityListYn: customListEnabled,
		restrictToCustomList: isResponsive && restrictToCustomList,
		cityListFormat,
		isResponsive,
	});

	const handleSave = async () => {
		setSubmitted(true);
		if (hasErrors) {
			return;
		}
		setSaving(true);
		try {
			// .unwrap() so a rejected save actually throws into the catch below — the thunk promise
			// resolves even on failure otherwise, which would falsely report success. (MAX-3950)
			await dispatch(saveLocations(buildPayload()) as any).unwrap();
			setSnackbarMessage("Location settings saved");
			setSnackbarSeverity("success");
			setSnackbarOpen(true);
			setDirty(false);
			setSubmitted(false);
		} catch{
			setSnackbarMessage("Failed to save location settings");
			setSnackbarSeverity("error");
			setSnackbarOpen(true);
		} finally {
			setSaving(false);
		}
	};

	const handleCancel = () => {
		if (saved) {
			syncFromSaved(saved);
		}
		setDirty(false);
		setSubmitted(false);
	};

	// Link to the client's live search page (same pattern as settings-search-cities).
	const listingsPage =
		pages?.find((p) => p.key === "LISTING_SEARCH_FORM")
		?? pages?.find((p) => p.type.id === PageType.LISTINGS.id);
	const viewSearchUrl = listingsPage ? user.baseUrl + listingsPage.path : undefined;

	return (
		<DashboardLayout
			permitted={user && (user.permissions.listingSearch || user.permissions.listingMapSearch)}
			title={title}
			header={
				<PageHeader
					title={title}
					description="Customize what appears in the search dropdown on your website, including default cities and additional location types."
					action={
						viewSearchUrl && (
							<Mui.Link href={viewSearchUrl} target="_blank" rel="noopener noreferrer">
								View Search <Icons.ExternalLink size={14} />
							</Mui.Link>
						)
					}
				/>
			}
		>
			<UnsavedChangesPrompt when={dirty} history={history} />

			{loading && <Mui.LinearProgress />}

			{/* --- Location Types --- */}
			{/* CF gates the Location Customization card to applicationVersionID==4 clients only
				(fusion_navigation.cfm:472-476), which maps to permissions.version===9. The Cities
				card below stays ungated, matching CF (Search Cities is not version-gated). */}
			{user.permissions.version === 9 && (
			<Mui.Box mb={2}>
				<FullPageCard>
					<Mui.Typography className={classes.sectionHeader}>Location Types</Mui.Typography>
					<Mui.Typography variant="body2" className={`${classes.sectionDescription} ${classes.locationTypesDescription}`}>
						Add location types to the search dropdown on your website, allowing visitors to search by areas like subdivisions, school districts, and neighborhoods. Available options vary by MLS board.
					</Mui.Typography>

					{boards.map((board, boardIndex) => (
						<Mui.Box key={`${board.boardId}-${boardIndex}`} className={classes.boardGroup}>
							{boardIndex > 0 && <Mui.Divider className={classes.boardDivider} />}
							<Mui.Typography className={classes.boardName}>{board.boardName}</Mui.Typography>
							{board.locationTypes.map((type) => {
								const errorKey = `${boardIndex}:${type.metaField}`;
								const showBlankError = submitted && blankDisplayNames.has(errorKey);
								return (
									<Mui.Box key={type.metaField} className={classes.typeRow}>
										{/* Figma 1180:16646 — checkbox, then a single column holding the label and
											everything under it, so the two cannot drift out of alignment. */}
										<Mui.Checkbox
											className={classes.typeCheckboxControl}
											color="secondary"
											checked={type.selected}
											onChange={(e) => updateType(boardIndex, type.metaField, { selected: e.target.checked })}
											inputProps={{ id: `type-${errorKey}` }}
										/>
										<Mui.Box className={classes.typeDetail}>
											{/* htmlFor keeps the label clickable now that it is no longer inside FormControlLabel. */}
											<Mui.Typography
												component="label"
												htmlFor={`type-${errorKey}`}
												className={classes.typeLabel}
											>
												{type.defaultLabel}
											</Mui.Typography>
											<IncludedLocations
												count={type.includedLocations.length}
												locations={type.includedLocations}
												classes={classes}
											/>
											{type.selected && (
												<Mui.Box className={classes.displayNameBox}>
													<Mui.Box className={classes.displayNameLabel}>
														<Mui.Typography variant="body2" className={classes.displayNameLabelText}>Display Name</Mui.Typography>
														<Mui.Tooltip title="Controls how this location type is labeled in the search dropdown.">
															<span className={classes.infoIcon}><Icons.Info size={14} /></span>
														</Mui.Tooltip>
													</Mui.Box>
													<Mui.TextField
														fullWidth
														variant="outlined"
														size="small"
														value={type.displayName}
														error={showBlankError}
														helperText={showBlankError ? "Display name is required." : undefined}
														onChange={(e) => updateType(boardIndex, type.metaField, { displayName: e.target.value })}
													/>
												</Mui.Box>
											)}
										</Mui.Box>
									</Mui.Box>
								);
							})}
						</Mui.Box>
					))}
				</FullPageCard>
			</Mui.Box>
			)}

			{/* --- Cities --- */}
			<Mui.Box mb={2}>
				<FullPageCard>
					<Mui.Typography className={classes.sectionHeader}>Cities</Mui.Typography>

					<Mui.Box className={classes.subSection}>
						<Mui.Typography className={classes.fieldLabel}>Format</Mui.Typography>
						<Mui.Typography variant="body2" className={`${classes.sectionDescription} ${classes.formatDescription}`}>
							Choose how cities appear in the search dropdown.
						</Mui.Typography>
						<Mui.RadioGroup
							value={String(cityListFormat)}
							onChange={(e) => { setCityListFormat(parseInt(e.target.value, 10)); setDirty(true); }}
						>
							{CITY_LIST_FORMAT_OPTIONS.map((opt) => (
								<Mui.FormControlLabel
									key={opt.value}
									value={String(opt.value)}
									control={<Mui.Radio color="secondary" />}
									label={opt.label}
								/>
							))}
						</Mui.RadioGroup>
					</Mui.Box>

					<Mui.Divider className={classes.subSectionDivider} />

					<Mui.Box className={classes.subSection}>
						<Mui.Typography className={classes.customCityListLabel}>Custom City List</Mui.Typography>
						<Mui.FormControlLabel
							className={classes.switchControl}
							control={
								<Mui.Switch
									color="secondary"
									checked={customListEnabled}
									onChange={(e) => { setCustomListEnabled(e.target.checked); setDirty(true); }}
								/>
							}
							label={
								<Mui.Box>
									<Mui.Typography className={classes.switchLabel}>Use custom city list</Mui.Typography>
									<Mui.Typography variant="body2" className={classes.sectionDescription}>
										When enabled, only your selected cities appear in the search dropdown by default. Website visitors can still search for any available city.
									</Mui.Typography>
								</Mui.Box>
							}
						/>

						{customListEnabled && (
							<Mui.Box mt={3} className={classes.customListDetail}>
								{isResponsive && (
									<Mui.FormControlLabel
										control={
											<Mui.Checkbox
												color="secondary"
												checked={restrictToCustomList}
												onChange={(e) => { setRestrictToCustomList(e.target.checked); setDirty(true); }}
											/>
										}
										label="Restrict Autosuggest & City Selector to Custom City List"
									/>
								)}
								<MultiSelectSearch
									options={cityOptions}
									selectedIds={cityIds}
									onChange={(ids) => { setCityIds(ids); setDirty(true); }}
									placeholder="Search to add cities"
									emptyText="No cities added yet!"
									error={submitted && cityError}
									helperText={submitted && cityError ? "At least one city must be selected." : undefined}
								/>
								{/* CF resetCustomCityList (SearchSettingsController.cfc:397-428) empties the list AND
									turns the custom-list feature OFF (setCustomCityList_YN(0)). Mirror both — clearing
									while leaving the toggle on would trip the "at least one city" validation. */}
								{cityIds.length > 0 && (
									<Mui.Link
										component="button"
										type="button"
										color="primary"
										underline="none"
										className={classes.clearAllButton}
										onClick={() => { setCityIds([]); setCustomListEnabled(false); setDirty(true); }}
										data-testid="search-cities-clear-all"
									>
										Clear all
									</Mui.Link>
								)}
							</Mui.Box>
						)}
					</Mui.Box>
				</FullPageCard>
			</Mui.Box>

			<SettingsStickyFooter
				dirty={dirty}
				saving={saving}
				authError={submitted && hasErrors ? "Please review errors before saving." : undefined}
				onSave={handleSave}
				onCancel={handleCancel}
			/>

			<Mui.Snackbar
				open={snackbarOpen}
				autoHideDuration={4000}
				onClose={() => setSnackbarOpen(false)}
			>
				<MuiAlert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
					{snackbarMessage}
				</MuiAlert>
			</Mui.Snackbar>
		</DashboardLayout>
	);
};

const NativePage = withStyles(NativePageBase, styles);

const SettingsLocationsPageBase: React.FC<Props> = (props) => {
	if (!coldFusionReplatform) {
		return <ColdFusionFallback {...props} />;
	}
	return <NativePage {...props} />;
};

export const SettingsLocationsPage = withStyles(SettingsLocationsPageBase, styles);
